import { useEffect, useState } from 'react';

const MANIFEST_DISCOVERY_TIMEOUT_MS = 1_500;

type ManifestExposure = {
  name?: unknown;
  path?: unknown;
};

export type CheckoutThemeManifestDecision =
  | { checkoutModulePath: string; status: 'remote' }
  | { status: 'native' }
  | { status: 'pending' };

type DiscoveryState = {
  hasCheckoutModule: boolean;
  key: string;
};

function normalizeModulePath(modulePath: string): string {
  return modulePath.replace(/^\.\//, '').replace(/\/+$/, '');
}

export function getCheckoutModulePath(modulePath: string): string {
  return `${normalizeModulePath(modulePath)}/checkout`;
}

export function manifestExposesCheckoutModule(manifest: unknown, checkoutModulePath: string): boolean {
  if (!manifest || typeof manifest !== 'object') return false;
  const { exposes } = manifest as { exposes?: unknown };
  if (!Array.isArray(exposes)) return false;

  return exposes.some((entry: unknown) => {
    if (!entry || typeof entry !== 'object') return false;
    const { name, path } = entry as ManifestExposure;
    return name === checkoutModulePath || path === `./${checkoutModulePath}`;
  });
}

async function fetchManifestDecision(
  manifestUrl: string,
  checkoutModulePath: string,
  signal: AbortSignal
): Promise<boolean> {
  try {
    const response = await fetch(manifestUrl, { signal });
    if (!response.ok) return false;
    return manifestExposesCheckoutModule(await response.json(), checkoutModulePath);
  } catch {
    // Discovery failures and the bounded timeout fail native for this mounted provider decision.
    return false;
  }
}

export type CheckoutThemeManifestInput = {
  manifestUrl: string;
  modulePath: string;
} | null;

export function useCheckoutThemeManifestDecision(input: CheckoutThemeManifestInput): CheckoutThemeManifestDecision {
  const manifestUrl = input?.manifestUrl ?? '';
  const checkoutModulePath = input ? getCheckoutModulePath(input.modulePath) : '';
  const key = input ? `${manifestUrl}\n${checkoutModulePath}` : '';
  const [state, setState] = useState<DiscoveryState | null>(null);

  useEffect(() => {
    if (!key || !manifestUrl || !checkoutModulePath) return undefined;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), MANIFEST_DISCOVERY_TIMEOUT_MS);
    let active = true;

    void fetchManifestDecision(manifestUrl, checkoutModulePath, controller.signal)
      .then((hasCheckoutModule) => {
        if (active) setState({ hasCheckoutModule, key });
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      active = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [checkoutModulePath, key, manifestUrl]);

  if (!key) return { status: 'native' };
  if (state?.key !== key) return { status: 'pending' };
  return state.hasCheckoutModule ? { checkoutModulePath, status: 'remote' } : { status: 'native' };
}
